import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Chip, List, ListItemButton,
  TextField, IconButton, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Feedback as FeedbackIcon, ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { feedbackService } from '../../services/feedbackService';
import FeedbackMessages from '../../components/feedback/FeedbackMessages';

const when = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};
const parseContext = (raw) => { try { return JSON.parse(raw || '{}'); } catch { return {}; } };
const lastBody = (t) => (t.messages?.length ? t.messages[t.messages.length - 1].body : '');

/**
 * Admin review of feedback threads: read the conversation, reply, and resolve / reopen.
 */
const FeedbackList = () => {
  const { authenticatedFetch } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [showContext, setShowContext] = useState(false);

  const selected = threads.find((t) => t.id === selectedId) || null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setThreads(await feedbackService.adminListThreads(authenticatedFetch));
    } catch (err) {
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true); setError(null);
    try {
      await feedbackService.adminReply(selected.id, reply.trim(), authenticatedFetch);
      setReply('');
      await load();
    } catch (err) { setError(err.message || 'Failed to send reply'); } finally { setBusy(false); }
  };

  const toggleResolved = async () => {
    if (!selected) return;
    setBusy(true); setError(null);
    try {
      await feedbackService.setResolved(selected.id, selected.status !== 'RESOLVED', authenticatedFetch);
      await load();
    } catch (err) { setError(err.message || 'Failed to update status'); } finally { setBusy(false); }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {selected && (
          <IconButton size="small" onClick={() => setSelectedId(null)} aria-label="back">
            <BackIcon fontSize="small" />
          </IconButton>
        )}
        <FeedbackIcon color="primary" />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {selected ? 'Conversation' : 'User feedback'}
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : selected ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" variant="outlined" color={selected.type === 'BUG' ? 'error' : 'default'}
              label={selected.type === 'BUG' ? 'Problem' : 'Feedback'} />
            <Chip size="small" color={selected.status === 'RESOLVED' ? 'success' : 'warning'}
              label={selected.status === 'RESOLVED' ? 'Resolved' : 'Open'} />
            <Typography variant="caption" color="text.secondary">{selected.userEmail || 'no email'}</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {parseContext(selected.context).path || '-'}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={() => setShowContext((v) => !v)}>
              {showContext ? 'Hide context' : 'View context'}
            </Button>
            <Button size="small" variant="outlined" onClick={toggleResolved} disabled={busy}>
              {selected.status === 'RESOLVED' ? 'Reopen' : 'Mark resolved'}
            </Button>
          </Box>
          {showContext && (
            <Box component="pre" sx={{
              m: 0, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: '0.75rem',
              maxHeight: 240, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {JSON.stringify(parseContext(selected.context), null, 2)}
            </Box>
          )}
          <Divider />
          <FeedbackMessages messages={selected.messages} mineSender="ADMIN" otherLabel="User" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" fullWidth placeholder="Reply to the user…" value={reply} disabled={busy}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} />
            <Button variant="contained" onClick={sendReply} disabled={busy || !reply.trim()}>Send</Button>
          </Box>
        </Box>
      ) : threads.length === 0 ? (
        <Alert severity="info">No feedback yet.</Alert>
      ) : (
        <List disablePadding>
          {threads.map((t) => (
            <ListItemButton key={t.id} divider onClick={() => { setSelectedId(t.id); setReply(''); }}>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip size="small" variant="outlined" color={t.type === 'BUG' ? 'error' : 'default'}
                    label={t.type === 'BUG' ? 'Problem' : 'Feedback'} />
                  <Chip size="small" color={t.status === 'RESOLVED' ? 'success' : 'warning'}
                    label={t.status === 'RESOLVED' ? 'Resolved' : 'Open'} />
                  <Typography variant="caption" color="text.secondary">{t.userEmail || 'no email'}</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary">{when(t.updatedAt)}</Typography>
                </Box>
                <Typography variant="body2" noWrap>{lastBody(t)}</Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default FeedbackList;
